import React, { useState } from 'react';
import LaboratoryList from '../../LaboratoryList/LaboratoryList';
import LaboratoryRequestList from '../../LaboratoryRequestsList/LaboratoryRequestsList';
import LaboratoryRequestCreateForm, {
	LaboratoryRequestFormDataScheme,
} from '../../LaboratoryRequestCreate/LaboratoryRequestCreateForm';
import LaboratoryRequestTemplateNameForm from '../LaboratoryRequestTemplateName/LaboratoryRequestTemplateNameForm';
import {
	CreateLaboratoryRequisitionTemplate,
	Laboratory,
	useCreateLaboratoryRequisitionTemplateMutation,
} from '../../../../../graphql/graphql';
import { LaboratoryTemplateType } from '../../../Enums/LaboratoryTemplateType.enum';
import { Size } from '../../../../../Enums/Size.enum';
import useApiClient from '../../../../Base/Hooks/useApiClient';
import LaboratoryRequestTemplateTypeSelection from '../LaboratoryRequestTemplateTypeSelection/LaboratoryRequestTemplateTypeSelection';

interface Props {
	workplaceId: string;
	doctorId: string;
	changeModalSize: (size: Size) => void;
	onCreate: (templateType: LaboratoryTemplateType) => void;
}

enum LaboratoryRequestTemplateCreateStatus {
	TYPE_SELECTION = 'type_selection',
	LABORATORY_SELECTION = 'laboratory_selection',
	REQUEST_SELECTION = 'request_selection',
	REQUEST_FILL = 'form_fill',
	TEMPLATE_NAME = 'template_name',
}

const LaboratoryRequestTemplateCreate: React.FC<Props> = ({
	workplaceId,
	doctorId,
	changeModalSize,
	onCreate,
}) => {
	const [state, setState] = useState<LaboratoryRequestTemplateCreateStatus>(
		LaboratoryRequestTemplateCreateStatus.TYPE_SELECTION,
	);
	const [type, setType] = useState<LaboratoryTemplateType | undefined>(
		undefined,
	);
	const [selectedLaboratoryId, setSelectedLaboratoryId] = useState<
		string | undefined
	>(undefined);
	const [selectedFormId, setSelectedFormId] = useState<string | undefined>(
		undefined,
	);
	const [formData, setFormData] = useState<
		LaboratoryRequestFormDataScheme[] | undefined
	>(undefined);
	const [createLaboratoryTemplate] =
		useCreateLaboratoryRequisitionTemplateMutation();
	const { displayError, displayGQLError } = useApiClient();

	const selectType = (type: LaboratoryTemplateType) => {
		setType(type);
		setState(LaboratoryRequestTemplateCreateStatus.LABORATORY_SELECTION);
	};

	const selectLaboratory = (laboratory: Laboratory) => {
		setSelectedLaboratoryId(laboratory.id);
		setState(LaboratoryRequestTemplateCreateStatus.REQUEST_SELECTION);
	};

	const selectRequest = (wizardId: string) => {
		setSelectedFormId(wizardId);
		setState(LaboratoryRequestTemplateCreateStatus.REQUEST_FILL);
		changeModalSize(Size.FULLSCREEN);
	};

	const formDataSuccess = (values: LaboratoryRequestFormDataScheme[]) => {
		setFormData(values);
		setState(LaboratoryRequestTemplateCreateStatus.TEMPLATE_NAME);
		changeModalSize(Size.MD);
	};

	const handleCreate = async (name: string) => {
		if (!selectedFormId) return;
		if (!type) return;
		try {
			const input: CreateLaboratoryRequisitionTemplate = {
				laboratoryRequisitionId: selectedFormId,
				name,
				data: {
					values: formData,
				},
			};

			if (type === LaboratoryTemplateType.WORKPLACE) {
				input.workplaceId = workplaceId;
			} else if (type === LaboratoryTemplateType.DOCTOR) {
				input.doctorId = doctorId;
			}

			const data = await createLaboratoryTemplate({
				input,
			}).unwrap();
			displayGQLError(data);
			if (data) {
				onCreate(type);
			}
		} catch (e) {
			displayError(e);
		}
	};

	switch (state) {
		case LaboratoryRequestTemplateCreateStatus.TYPE_SELECTION:
			return <LaboratoryRequestTemplateTypeSelection onClick={selectType} />;
		case LaboratoryRequestTemplateCreateStatus.LABORATORY_SELECTION:
			return <LaboratoryList onClick={selectLaboratory} />;
		case LaboratoryRequestTemplateCreateStatus.REQUEST_SELECTION:
			if (selectedLaboratoryId) {
				return (
					<LaboratoryRequestList
						laboratoryId={selectedLaboratoryId}
						onClick={selectRequest}
						buttonLabel='component.laboratoryRequestTemplateCreate.selectLaboratoryRequest'
					/>
				);
			}
			break;
		case LaboratoryRequestTemplateCreateStatus.REQUEST_FILL:
			if (selectedFormId) {
				return (
					<LaboratoryRequestCreateForm
						onSuccess={formDataSuccess}
						formId={selectedFormId}
						workplaceId={workplaceId}
					/>
				);
			}
			break;
		case LaboratoryRequestTemplateCreateStatus.TEMPLATE_NAME:
			return <LaboratoryRequestTemplateNameForm onSuccess={handleCreate} />;
	}

	return <></>;
};

export default LaboratoryRequestTemplateCreate;
